import { useEffect, useState } from "react";
import { Download, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { AdminTitle } from "@/admin/AdminLayout";

const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const Composer = ({ confirmed, onSent }) => {
  const [m, setM] = useState({ subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Send "${m.subject}" to ${confirmed} confirmed subscriber(s)?`)) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/newsletter/send", m); toast.success(`Sending to ${data.recipients} subscriber(s)`); setM({ subject: "", body: "" }); onSent(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} data-testid="newsletter-composer" className="vellum p-6 grid gap-4">
      <p className="eyebrow">Send an update</p>
      <p className="text-sm italic text-[#4a3a2a] -mt-2">Goes to confirmed subscribers only. An unsubscribe link is added automatically. Leave a blank line between paragraphs.</p>
      <label><span className="admin-label">Subject</span><input data-testid="newsletter-subject" required maxLength={150} className="admin-input" value={m.subject} onChange={(e) => setM({ ...m, subject: e.target.value })} /></label>
      <label><span className="admin-label">Message</span><textarea data-testid="newsletter-body" required rows={8} className="admin-input" value={m.body} onChange={(e) => setM({ ...m, body: e.target.value })} /></label>
      <button data-testid="newsletter-send" disabled={busy || !confirmed} className="btn-illum theme-flame justify-self-start disabled:opacity-50"><Send className="w-4 h-4" /> {busy ? "Sending…" : `Send to ${confirmed} subscriber(s)`}</button>
    </form>
  );
};

export default function AdminNewsletter() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/subscribers").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const exportCsv = async () => {
    const r = await api.get("/admin/subscribers/export", { responseType: "blob" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(r.data), download: "newsletter-subscribers.csv" });
    a.click(); URL.revokeObjectURL(a.href);
  };
  const remove = async (s) => { if (!window.confirm(`Remove ${s.email}?`)) return; try { await api.delete(`/admin/subscribers/${s.id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!d) return <p className="font-display text-2xl">Unrolling…</p>;
  return (
    <div data-testid="admin-newsletter">
      <AdminTitle eyebrow="Book release news" title="Newsletter">
        <button data-testid="newsletter-export" onClick={exportCsv} className="btn-ghost-illum"><Download className="w-4 h-4" /> Export spreadsheet (CSV)</button>
      </AdminTitle>
      <div className="grid grid-cols-3 gap-4 mb-8">
        {Object.entries(d.counts).map(([k, n]) => <div key={k} data-testid={`newsletter-count-${k}`} className="vellum p-4 text-center"><p className="font-display text-3xl">{n}</p><p className="font-heading text-xs uppercase tracking-wider">{k}</p></div>)}
      </div>
      <div className="grid xl:grid-cols-2 gap-6">
        <Composer confirmed={d.counts.confirmed} onSent={load} />
        <section className="vellum p-6">
          <p className="eyebrow mb-3">Sent updates</p>
          {d.sends.length === 0 ? <p className="italic text-sm">Nothing sent yet.</p> : d.sends.map((s) => (
            <p key={s.id} data-testid="newsletter-send-row" className="py-2 border-b border-[#c9a227]/25 text-sm"><b>{s.subject}</b> · {fmt(s.sent_at)} · {s.delivered ?? "sending…"}/{s.recipients} delivered</p>
          ))}
        </section>
      </div>
      <section className="vellum mt-6 overflow-x-auto">
        <table data-testid="newsletter-table" className="w-full text-sm">
          <thead><tr className="text-left font-heading text-xs uppercase tracking-wider border-b border-[#c9a227]"><th className="p-3">Email</th><th className="p-3">Status</th><th className="p-3">Signed up</th><th className="p-3">Confirmed</th><th className="p-3" /></tr></thead>
          <tbody>
            {d.subscribers.length === 0 && <tr><td colSpan={5} className="p-6 italic text-center">No subscribers yet.</td></tr>}
            {d.subscribers.map((s) => (
              <tr key={s.id} className="border-b border-[#c9a227]/20">
                <td className="p-3">{s.email}</td><td className="p-3 capitalize">{s.status}</td><td className="p-3">{fmt(s.created_at)}</td><td className="p-3">{fmt(s.confirmed_at)}</td>
                <td className="p-3 text-right"><button data-testid={`newsletter-remove-${s.email}`} onClick={() => remove(s)} className="p-2 hover:bg-[#991b1b]/10 text-[#991b1b]" title="Remove"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
